import React from "react";
import { InputBaseProps } from "..";
import { SanityInputRadioButtonsProps } from "../../../models/form/SanityFormInputField";
import { ControlledRadioButtons } from "../../ControlledInputs/ControlledRadioButtons";
import { Inputs } from "../Inputs";
import { useFieldValidator } from "../Validation";
import { renderHiddenOptionValues } from "./DynamicInputs";
import InputContainer from "./InputContainer";
import { useFieldCustom } from "./useFieldCustom";

export type RadioButtonsInvertedProps = InputBaseProps<SanityInputRadioButtonsProps> & {
  vertical?: boolean;
};

const RadioButtonsInverted = (props: RadioButtonsInvertedProps) => {
  const {
    inputContainerVariant,
    name,
    label,
    options,
    readonly,
    validation,
    vertical = false
  } = props;

  const validator = useFieldValidator(validation);

  const [field, meta, _helpers] = useFieldCustom({
    name,
    validate: validator
  });

  const onChange: React.InputHTMLAttributes<HTMLInputElement>["onChange"] = e => {
    field.onChange(e);
  };

  const error = meta.touched && !!meta.error;

  const optionsWithSubfields = options.filter(option => option.inputs?.length);
  const selectedOptionsWithSubfields = optionsWithSubfields.filter(
    option => option.value === meta.value
  );
  const notSelectedOptionsWithSubfields = optionsWithSubfields.filter(
    option => option.value !== meta.value
  );

  return (
    <>
      <InputContainer meta={meta} variant={inputContainerVariant}>
        <ControlledRadioButtons
          error={error}
          label={label}
          largeLabel={inputContainerVariant === "sectioned"}
          name={name}
          onChange={onChange}
          options={options.map(option =>
            typeof option === "string"
              ? {
                  label: option,
                  value: option
                }
              : {
                  label: option.label ?? option.value,
                  value: option.value
                }
          )}
          readonly={readonly}
          value={meta.value}
          variant={vertical ? "inverted-vertical" : "inverted"}
        />
      </InputContainer>

      {!!selectedOptionsWithSubfields.length && (
        <div className="border-l border-input-border pl-4">
          {selectedOptionsWithSubfields.map((option, index) => (
            <Inputs
              inputs={option.inputs ?? []}
              key={index}
              readonly={readonly ?? false}
            />
          ))}
        </div>
      )}

      {!!notSelectedOptionsWithSubfields.length &&
        renderHiddenOptionValues(notSelectedOptionsWithSubfields)}
    </>
  );
};

export default RadioButtonsInverted;
